/**
 * Helpers for reading claims out of the MSAL id token.
 *
 * Authorization is driven by the app-role names in the `roles` claim (see
 * getUserRoleFromToken); the `groups` helper exists only for diagnostics.
 */
import type { AccountInfo } from '@azure/msal-browser';
import type { UserRole } from '../services/types';

export interface IdTokenClaims {
  /** App-role names assigned in the app registration (e.g. `app-brr-admin`). */
  roles?: string[];
  /** AAD security-group object IDs the user belongs to (GUIDs). */
  groups?: string[];
  [key: string]: unknown;
}

/**
 * Hand-rolled base64url decode to avoid pulling in a JWT library for the one
 * thing we need. Exported chiefly for tests and for the rare path where only a
 * raw token string (not MSAL's parsed claims) is available.
 */
export function decodeJwtPayload(idToken: string | undefined | null): IdTokenClaims | null {
  if (!idToken) return null;
  const parts = idToken.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json) as IdTokenClaims;
  } catch {
    return null;
  }
}

/**
 * MSAL normally exposes already-parsed `idTokenClaims`; the raw-token decode is
 * only a fallback for accounts that carry just a raw `idToken` string.
 */
export function getIdTokenClaims(account: AccountInfo | null | undefined): IdTokenClaims {
  if (!account) return {};
  if (account.idTokenClaims) {
    return account.idTokenClaims as IdTokenClaims;
  }
  const rawIdToken = (account as { idToken?: string }).idToken;
  return decodeJwtPayload(rawIdToken) ?? {};
}

/**
 * Role names are returned verbatim so callers can log exactly what the token
 * carried; the case-insensitive matching happens in getUserRoleFromToken.
 */
export function getTokenRoles(account: AccountInfo | null | undefined): string[] {
  const roles = getIdTokenClaims(account).roles;
  return Array.isArray(roles) ? roles : [];
}

/**
 * Group membership no longer drives authorization (role names do). Retained for
 * diagnostics when a token's role claim looks wrong.
 */
export function getTokenGroups(account: AccountInfo | null | undefined): string[] {
  const groups = getIdTokenClaims(account).groups;
  return Array.isArray(groups) ? groups.map((g) => String(g).toLowerCase()) : [];
}

/**
 * App-role names that grant each role in the current subscription. These are the
 * single source of truth for role resolution — if a role is renamed in the Entra
 * ID app registration, update the name here.
 */
export const ROLE_CLAIM_NAMES: Record<UserRole, string> = {
  admin: 'app-brr-admin',
  supervisor: 'app-brr-supervisor',
  viewer: 'app-brr-viewer',
};

/**
 * Resolve the app's 3-tier UserRole from the role names in the token's `roles`
 * claim.
 *
 * The highest privilege a user holds wins (a user with several roles assigned
 * gets the strongest one). If the token carries none of the known role names we
 * fall back to 'viewer' (least privilege) and warn — so a missing/misconfigured
 * roles claim surfaces loudly instead of silently mis-authorizing.
 */
export function getUserRoleFromToken(account: AccountInfo | null | undefined): UserRole {
  const roles = getTokenRoles(account).map((r) => r.toLowerCase());
  const hasRole = (name: string) => roles.includes(name.toLowerCase());

  if (hasRole(ROLE_CLAIM_NAMES.admin)) return 'admin';
  if (hasRole(ROLE_CLAIM_NAMES.supervisor)) return 'supervisor';
  if (hasRole(ROLE_CLAIM_NAMES.viewer)) return 'viewer';

  if (roles.length > 0) {
    console.warn('[BRR][MSAL] Token roles matched no known app role, defaulting to viewer:', roles);
  } else {
    console.warn('[BRR][MSAL] Token carried no roles[] claim, defaulting to viewer');
  }
  return 'viewer';
}
